import { useMutation } from "@tanstack/react-query";
import { some } from "lodash";
import { Barcode, ReceiptText } from "lucide-react";
import { useCallback } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { issueInvoice } from "@/api";
import { Button, ShowContainer, ShowSection } from "@/components";
import { useBreadCrumbContext } from "@/contexts/breadcrumb";
import {
	formatDataPrinterInvoice,
	formatDataPrinterReceipt,
	validationErrors,
} from "@/helpers";
import { formatLongDatetime } from "@/helpers/dates";
import { documentNumber } from "@/helpers/document-number";
import {
	useCurrentStore,
	usePrint,
	useReceiptColumns,
	useSale,
	useSalePayments,
	useSaleProducts,
} from "../../hooks";
import SaleInfos from "./infos";
import SaleProducts from "./products";

const Sale = () => {
	const { t } = useTranslation();
	const { id } = useParams();
	const { data: saleData } = useSale(id);
	const sale = saleData?.data;

	const { data: saleProductsData } = useSaleProducts({
		"q[saleIdEq]": sale?.id,
	});
	const { data: salePaymentsData } = useSalePayments(
		{ "q[saleIdEq]": sale?.id },
		!!sale?.id,
	);

	const store = useCurrentStore();

	useBreadCrumbContext([
		{ label: t("sales.pageTitle"), url: "/sales" },
		{
			label: t("sale.pageTitle", { saleNumber: documentNumber(sale?.number) }),
		},
	]);

	const { mutate: print } = usePrint();
	const columns = useReceiptColumns();

	const onPrintReceipt = useCallback(() => {
		if (sale && saleProductsData && salePaymentsData && store) {
			print(
				formatDataPrinterReceipt({
					sale,
					saleProducts: saleProductsData.data,
					salePayments: salePaymentsData.data,
					store,
					t,
					columns,
				}),
			);
		}
	}, [sale, saleProductsData, salePaymentsData, store, t, print, columns]);

	// Issuing is idempotent: printing again reprints the invoice already issued,
	// with the same number and the seller and buyer as they were then.
	const { mutate: printInvoice } = useMutation({
		mutationFn: (saleId: string) => issueInvoice(saleId),
		onSuccess: ({ data: invoice }) => {
			if (sale && saleProductsData && salePaymentsData) {
				print(
					formatDataPrinterInvoice({
						invoice,
						sale,
						saleProducts: saleProductsData.data,
						salePayments: salePaymentsData.data,
						t,
						columns,
					}),
				);
			}
		},
		onError: (error) => {
			const reason = validationErrors(error).sale;
			toast.error(
				reason ? t("sale.invoiceRefused", { reason }) : t("global.savingError"),
			);
		},
	});

	if (!sale) {
		return null;
	}

	// The API refuses all three; saying why here beats a failed print.
	const invoiceUnavailable = sale.imported
		? t("sale.invoiceUnavailableImported")
		: some(salePaymentsData?.data, (payment) => payment.paymentType?.isRefund)
			? t("sale.invoiceUnavailableRefund")
			: sale.customer
				? undefined
				: t("sale.invoiceUnavailable");

	return (
		<ShowContainer
			title={t("sale.pageTitle", { saleNumber: documentNumber(sale.number) })}
			subtitle={t("sale.pageSubtitle", {
				saleDate: formatLongDatetime(sale.createdAt),
			})}
			button={
				<div className="flex gap-2 items-center">
					<Button variant="outline" type="button" onClick={onPrintReceipt}>
						<Barcode />
						{t("sale.printReceipt")}
					</Button>
					<Button
						type="button"
						onClick={() => printInvoice(sale.id)}
						disabled={!!invoiceUnavailable}
						title={invoiceUnavailable}
					>
						<ReceiptText />
						{t("sale.printInvoice")}
					</Button>
				</div>
			}
		>
			<ShowSection title={t("sale.generalInformations")}>
				<SaleInfos sale={sale} />
			</ShowSection>

			<ShowSection title={t("sale.products")}>
				<SaleProducts saleProductsData={saleProductsData?.data} />
			</ShowSection>
		</ShowContainer>
	);
};

export default Sale;
